import type { Job } from "@malfak/contracts";
import { useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { ChevronRight, ListChecks } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList } from "@/components/ui/controls";
import { Num } from "@/components/ui/misc";
import { Page, PageHeader } from "@/components/ui/page";
import { EmptyState, ErrorState, ListSkeleton } from "@/components/ui/states";
import { useApiQuery } from "@/lib/api/hooks";
import { fmtNumber, fmtRelative } from "@/lib/format";
import { useT } from "@/lib/i18n";
import { cn } from "@/lib/utils";
import { JobControls } from "./job-controls";
import { JobDrawer } from "./job-drawer";
import { JobError } from "./job-error";
import { AiJobResult } from "./ai-result";
import { BackfillDetails } from "./backfill-details";
import { backfillProgressOf, CATEGORY_ICON, countsText, etaText, isActive, jobKindLabel, STATUS_STROKE, STATUS_TONE, linkTo, targetHref } from "./model";
import { ProgressBar, ProgressRing } from "./progress-ring";
import { useJobCounts } from "./use-jobs";

export type JobsTab = "active" | "finished" | "failed" | "all";
export type JobsSearch = { tab?: JobsTab; category?: Job["category"]; job?: string };
const TABS: JobsTab[] = ["active", "finished", "failed", "all"];
const CATEGORIES: Job["category"][] = ["sync", "bulk", "ai", "publish", "export", "other"];

export function validateJobsSearch(s: Record<string, unknown>): JobsSearch {
  const tab = TABS.includes(s.tab as JobsTab) ? (s.tab as JobsTab) : undefined;
  const category = CATEGORIES.includes(s.category as Job["category"]) ? (s.category as Job["category"]) : undefined;
  const job = typeof s.job === "string" && /^[0-9a-f-]{36}$/i.test(s.job) ? s.job : undefined;
  return { ...(tab ? { tab } : {}), ...(category ? { category } : {}), ...(job ? { job } : {}) };
}

/** Jobs center (ALP-67): every long operation with live progress, ETA, controls and failure reports. */
export function JobsPage() {
  const { t } = useT();
  const search = useSearch({ strict: false }) as JobsSearch;
  const navigate = useNavigate();
  const tab: JobsTab = search.tab ?? "all";
  const category = search.category;
  const set = (patch: Partial<JobsSearch>) => void navigate({ to: "/jobs", search: ((prev: JobsSearch) => validateJobsSearch({ ...prev, ...patch })) as never, replace: true });
  const { data: counts } = useJobCounts();
  const { data, isLoading, error, refetch } = useApiQuery("jobs", { query: { status: tab, limit: 100, ...(category ? { category } : {}) } }, { staleTime: 5_000, refetchInterval: 20_000 });
  const jobs = data?.items ?? [];

  return (
    <Page>
      <PageHeader title={t("jobs.title")} description={t("jobs.description")} />
      <Tabs value={tab} onValueChange={(v) => set({ tab: v as JobsTab })}>
        <div className="mb-4 flex flex-col gap-3">
          <TabsList
            layoutId="jobs-tabs"
            value={tab}
            items={TABS.map((k) => ({ value: k, label: t(`jobs.tabs.${k}`), ...(k === "active" && counts ? { count: counts.active } : k === "failed" && counts?.failed24h ? { count: counts.failed24h } : {}) }))}
          />
          <div className="flex flex-wrap gap-1.5" role="group" aria-label={t("jobs.categories.all")}>
            <Chip on={!category} onClick={() => set({ category: undefined })}>
              {t("jobs.categories.all")}
            </Chip>
            {CATEGORIES.map((c) => {
              const Icon = CATEGORY_ICON[c];
              return (
                <Chip key={c} on={category === c} onClick={() => set({ category: category === c ? undefined : c })}>
                  <Icon className="size-3.5" aria-hidden />
                  {t(`jobs.categories.${c}`)}
                </Chip>
              );
            })}
          </div>
        </div>

        <TabsContent value={tab} className="outline-none" tabIndex={-1}>
          {isLoading ? (
            <ListSkeleton rows={5} />
          ) : error ? (
            <ErrorState error={error} onRetry={() => void refetch()} />
          ) : !jobs.length ? (
            <EmptyState icon={ListChecks} title={t(`jobs.empty.${tab}.title`)} hint={t(`jobs.empty.${tab}.hint`)} />
          ) : (
            <ul className="space-y-2.5" data-testid="jobs-list">
              <AnimatePresence initial={false}>
                {jobs.map((j, i) => (
                  <JobRow key={j.id} job={j} index={i} onOpen={() => set({ job: j.id })} />
                ))}
              </AnimatePresence>
            </ul>
          )}
        </TabsContent>
      </Tabs>
      <JobDrawer jobId={search.job ?? null} onClose={() => set({ job: undefined })} />
    </Page>
  );
}

function Chip({ on, onClick, children }: { on: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      type="button"
      aria-pressed={on}
      onClick={onClick}
      className={cn(
        "inline-flex h-8 items-center gap-1.5 rounded-full border px-3 text-[12.5px] font-medium transition",
        on ? "border-transparent bg-accent-soft text-accent-ink" : "border-line text-ink-2 hover:bg-surface-2 hover:text-ink",
      )}
    >
      {children}
    </button>
  );
}

function JobRow({ job, index, onOpen }: { job: Job; index: number; onOpen: () => void }) {
  const { t } = useT();
  const reduce = useReducedMotion();
  const qc = useQueryClient();
  const Icon = CATEGORY_ICON[job.category];
  const kind = jobKindLabel(job.kind, t);
  const eta = etaText(job, t);
  const href = targetHref(job, qc);
  const active = isActive(job.status);
  return (
    <motion.li
      layout={!reduce}
      initial={reduce ? false : { opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={reduce ? undefined : { opacity: 0, scale: 0.98 }}
      transition={{ duration: 0.25, delay: reduce ? 0 : Math.min(index, 8) * 0.03 }}
      className={cn("group rounded-2xl border bg-surface p-4 shadow-xs transition-colors", active ? "border-[color-mix(in_srgb,var(--accent)_35%,var(--line))]" : "border-line")}
      data-testid="job-row"
      data-job-id={job.id}
      data-status={job.status}
    >
      <div className="flex items-start gap-3">
        <ProgressRing value={active ? job.progress : (job.progress ?? 1)} size={44} stroke={4} color={STATUS_STROKE[job.status]} glow={job.status === "running"}>
          <Icon className="size-4 text-ink-2" />
        </ProgressRing>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-[14px] font-semibold text-ink">{kind}</span>
            {job.target?.label && (
              <>
                <span className="text-ink-3" aria-hidden>
                  ·
                </span>
                {href ? (
                  <Link {...linkTo(href)} data-testid="job-target-link" className="max-w-[320px] truncate text-[13.5px] text-ink-2 hover:text-ink hover:underline" title={job.target.label}>
                    {job.target.label}
                  </Link>
                ) : (
                  <span className="max-w-[320px] truncate text-[13.5px] text-ink-2" title={job.target.label}>
                    {job.target.label}
                  </span>
                )}
              </>
            )}
            <Badge tone={STATUS_TONE[job.status]} size="xs" data-testid="job-status">
              {t(`jobs.status.${job.status}`)}
            </Badge>
          </div>
          <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-[12px] text-ink-3">
            {backfillProgressOf(job) ? <BackfillDetails job={job} compact /> : <Num>{countsText(job, t)}</Num>}
            {job.failed > 0 && <span className="font-medium text-bad">{t("jobs.counts.failed", { count: fmtNumber(job.failed) })}</span>}
            {job.skipped > 0 && <span>{t("jobs.counts.skipped", { count: fmtNumber(job.skipped) })}</span>}
            {eta && <span className={cn(job.status === "paused" && "text-warn")}>{eta}</span>}
            <span>{fmtRelative(job.finishedAt ?? job.createdAt)}</span>
          </div>
          <div
            role="progressbar"
            aria-label={t("jobs.progressLabel", { title: kind })}
            aria-valuemin={0}
            aria-valuemax={100}
            {...(job.progress !== null ? { "aria-valuenow": Math.round(job.progress * 100) } : {})}
            className="mt-2.5"
          >
            <ProgressBar value={active ? job.progress : (job.progress ?? 1)} color={STATUS_STROKE[job.status]} running={job.status === "running"} />
          </div>
          <JobError job={job} />
          <AiJobResult job={job} compact />
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <JobControls job={job} />
            <Button size="xs" variant="ghost" className="ms-auto" iconEnd={<ChevronRight className="flip-rtl" />} onClick={onOpen} data-testid="job-details">
              {t("jobs.actions.details")}
            </Button>
          </div>
        </div>
      </div>
    </motion.li>
  );
}
